import { ReactNode, useEffect, useRef, useState } from 'react';
import { LucideIcon, TrendingUp, TrendingDown, Info, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDisplayValue } from '@/lib/formatNumber';
import { RAGStatus } from '@/lib/theme';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface TrendData {
  value: number;
  direction: 'up' | 'down';
}

interface SummaryMetricCardEditable {
  id: string;
  inputValue: string;
  onInputChange: (raw: string) => void;
  inputPlaceholder?: string;
  editAriaLabel?: string;
}

interface SummaryMetricCardProps {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  trend?: TrendData;
  status?: RAGStatus;
  isLoading?: boolean;
  className?: string;
  editable?: SummaryMetricCardEditable;
  labelHint?: string;
}

const statusValueClass: Record<RAGStatus, string> = {
  green: 'text-rag-good',
  amber: 'text-rag-warning',
  red: 'text-destructive',
};

const valueClassName =
  'text-2xl font-semibold tabular-nums tracking-tight';

export function SummaryMetricCard({
  label,
  value,
  icon: Icon,
  trend,
  status,
  isLoading = false,
  className,
  editable,
  labelHint,
}: SummaryMetricCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const snapshotRef = useRef('');

  const TrendIcon = trend?.direction === 'up' ? TrendingUp : TrendingDown;
  const trendColorClass =
    trend?.direction === 'up' ? 'text-rag-good' : 'text-destructive';

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const startEditing = () => {
    if (!editable) return;
    snapshotRef.current = editable.inputValue;
    setIsEditing(true);
  };

  const stopEditing = () => {
    setIsEditing(false);
  };

  const cancelEditing = () => {
    if (!editable) return;
    editable.onInputChange(snapshotRef.current);
    setIsEditing(false);
  };

  return (
    <div className={cn('bg-card px-4 py-3.5', className)}>
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        {Icon && (
          <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" strokeWidth={1.75} />
        )}
        <span>{label}</span>
        {labelHint ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex rounded-sm text-muted-foreground/80 transition-colors hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label={`About ${label}`}
                >
                  <Info className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[16rem] text-left">
                {labelHint}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>
      {isLoading ? (
        <div className="mt-1.5 h-7 w-16 animate-pulse rounded bg-muted" />
      ) : editable && isEditing ? (
        <Input
          ref={inputRef}
          id={editable.id}
          type="number"
          min={0}
          step="0.01"
          value={editable.inputValue}
          placeholder={editable.inputPlaceholder ?? '0.00'}
          onChange={(e) => editable.onInputChange(e.target.value)}
          onBlur={stopEditing}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              stopEditing();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelEditing();
            }
          }}
          className={cn(
            'mt-1 h-8 w-full border-border/60 bg-background px-0 py-0 text-2xl font-semibold tabular-nums tracking-tight shadow-none focus-visible:ring-1',
            status && statusValueClass[status]
          )}
        />
      ) : (
        <div className="mt-1 flex items-center gap-2">
          <p
            className={cn(valueClassName, status && statusValueClass[status])}
          >
            {formatDisplayValue(value)}
          </p>
          {editable ? (
            <button
              type="button"
              onClick={startEditing}
              className="inline-flex rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={editable.editAriaLabel ?? `Edit ${label}`}
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          ) : null}
        </div>
      )}
      {trend && !isLoading && !isEditing && (
        <div
          className={cn(
            'mt-1 flex items-center gap-1 text-xs font-medium',
            trendColorClass
          )}
        >
          <TrendIcon className="h-3 w-3" strokeWidth={1.75} />
          <span>{Math.abs(trend.value)}%</span>
        </div>
      )}
    </div>
  );
}

interface MetricGridProps {
  children: ReactNode;
  className?: string;
}

export function MetricGrid({ children, className }: MetricGridProps) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border lg:grid-cols-4',
        className
      )}
    >
      {children}
    </div>
  );
}

export default SummaryMetricCard;
